import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Users,
  Briefcase,
  Target,
  Clock,
  Award,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  UserCheck,
  Search,
  FileText,
  HeartHandshake,
  Zap,
  Shield,
  Globe,
  Building,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { openExternalLink } from "../../../services/site/linkService";
import { InfoService } from "../../../services/site/InfoService";
import "./Services.scss";

const StaffingSolutions: React.FC = () => {
  const navigate = useNavigate();

  // Hero Section
  const HeroSection: React.FC = () => {
    return (
      <section className="sd_service-hero">
        <div className="container">
          <motion.div
            className="sd_service-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="sd_service-badge"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Users size={20} /> Talent Acquisition
            </motion.div>

            <h1 className="sd_service-hero-title">
              Find the{" "}
              <span className="gradient-text">Perfect Talent</span> for Your
              Business
            </h1>
            <p className="sd_service-hero-subtitle">
              Premier staffing and recruitment solutions that connect top-tier
              talent with leading organizations. We specialize in IT staffing,
              contract placements, and permanent hiring across all industries.
            </p>

            <motion.div
              className="sd_service-tech-stack"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="sd_service-tech-icon">
                <Briefcase size={24} />
                <span>IT Staffing</span>
              </div>
              <div className="sd_service-tech-icon">
                <Clock size={24} />
                <span>Contract Roles</span>
              </div>
              <div className="sd_service-tech-icon">
                <UserCheck size={24} />
                <span>Permanent Hire</span>
              </div>
              <div className="sd_service-tech-icon">
                <Target size={24} />
                <span>Executive Search</span>
              </div>
            </motion.div>

            <div className="sd_service-hero-buttons">
              <button
                onClick={() => navigate("/contact-us")}
                className="btn btn-primary"
              >
                Hire Talent <ArrowRight size={20} />
              </button>
              <button
                onClick={() => navigate("/contact-us")}
                className="btn btn-secondary"
              >
                Find Jobs
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    );
  };

  // Staffing Services Grid
  const ServicesGrid: React.FC = () => {
    const services = [
      {
        icon: <Briefcase size={32} />,
        title: "IT Staffing Solutions",
        description:
          "Connect with skilled IT professionals including developers, engineers, architects, and technical specialists.",
        features: [
          "Software Developers",
          "DevOps Engineers",
          "Data Scientists",
          "Cloud Architects",
          "QA Engineers",
        ],
        color: "#4A90E2",
      },
      {
        icon: <Clock size={32} />,
        title: "Contract & Temporary Staffing",
        description:
          "Flexible staffing solutions for short-term projects, seasonal demands, and contract-to-hire opportunities.",
        features: [
          "Project-based Hiring",
          "Seasonal Workforce",
          "Contract-to-Hire",
          "Flexible Duration",
          "Quick Deployment",
        ],
        color: "#E94B3C",
      },
      {
        icon: <UserCheck size={32} />,
        title: "Permanent Placement",
        description:
          "Find the right full-time employees who align with your company culture and long-term goals.",
        features: [
          "Full-time Positions",
          "Cultural Fit Assessment",
          "Comprehensive Screening",
          "Replacement Guarantee",
          "Onboarding Support",
        ],
        color: "#50C878",
      },
      {
        icon: <Target size={32} />,
        title: "Executive Search",
        description:
          "Specialized recruitment for C-level executives, senior management, and leadership positions.",
        features: [
          "C-Suite Recruitment",
          "Leadership Roles",
          "Confidential Search",
          "Market Intelligence",
          "Succession Planning",
        ],
        color: "#9B59B6",
      },
      {
        icon: <Building size={32} />,
        title: "Managed Workforce Solutions",
        description:
          "End-to-end workforce management including recruitment, payroll, compliance, and administration.",
        features: [
          "Payroll Management",
          "Compliance Handling",
          "Performance Tracking",
          "Resource Planning",
          "Cost Optimization",
        ],
        color: "#F39C12",
      },
      {
        icon: <Globe size={32} />,
        title: "Global Talent Acquisition",
        description:
          "Access worldwide talent pool for international projects and remote work opportunities.",
        features: [
          "Remote Workforce",
          "International Hiring",
          "Visa Sponsorship Support",
          "Multi-timezone Coverage",
          "Cultural Integration",
        ],
        color: "#1ABC9C",
      },
    ];

    return (
      <section className="sd_service-grid-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Staffing Services</h2>
            <p className="section-subtitle">
              Comprehensive talent acquisition solutions for every need
            </p>
          </motion.div>

          <div className="sd_service-grid">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                className="sd_service-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <div
                  className="sd_service-card-icon"
                  style={{ color: service.color }}
                >
                  {service.icon}
                </div>
                <h3 className="sd_service-card-title">{service.title}</h3>
                <p className="sd_service-card-desc">{service.description}</p>
                <ul className="sd_service-card-features">
                  {service.features.map((feature, fIdx) => (
                    <li key={fIdx}>
                      <CheckCircle size={16} /> {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Industry Expertise
  const IndustryExpertise: React.FC = () => {
    const industries = [
      { name: "Information Technology", icon: "💻", count: "2000+" },
      { name: "Healthcare & Life Sciences", icon: "🏥", count: "800+" },
      { name: "Financial Services", icon: "💰", count: "1200+" },
      { name: "Manufacturing", icon: "🏭", count: "600+" },
      { name: "Retail & E-commerce", icon: "🛒", count: "500+" },
      { name: "Education & Training", icon: "🎓", count: "400+" },
      { name: "Telecommunications", icon: "📡", count: "450+" },
      { name: "Energy & Utilities", icon: "⚡", count: "300+" },
    ];

    return (
      <section className="sd_service-industry-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Industry Expertise</h2>
            <p className="section-subtitle">
              We serve diverse industries with specialized talent
            </p>
          </motion.div>

          <div className="sd_service-industry-grid">
            {industries.map((industry, idx) => (
              <motion.div
                key={idx}
                className="sd_service-industry-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="industry-icon">{industry.icon}</div>
                <h3 className="industry-name">{industry.name}</h3>
                <p className="industry-count">{industry.count} Placements</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Recruitment Process
  const RecruitmentProcess: React.FC = () => {
    const steps = [
      {
        number: "01",
        title: "Requirement Analysis",
        description:
          "We understand your needs, job requirements, culture, and ideal candidate profile.",
        icon: <Search size={32} />,
      },
      {
        number: "02",
        title: "Talent Sourcing",
        description:
          "Access our vast network and database to identify qualified candidates matching your criteria.",
        icon: <Users size={32} />,
      },
      {
        number: "03",
        title: "Screening & Assessment",
        description:
          "Rigorous evaluation including technical tests, background checks, and skills verification.",
        icon: <FileText size={32} />,
      },
      {
        number: "04",
        title: "Interview & Placement",
        description:
          "Coordinate interviews, negotiate offers, and ensure smooth onboarding of selected candidates.",
        icon: <HeartHandshake size={32} />,
      },
    ];

    return (
      <section className="sd_service-process-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Recruitment Process</h2>
            <p className="section-subtitle">
              A systematic approach to finding the perfect match
            </p>
          </motion.div>

          <div className="sd_service-process-timeline">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                className="sd_service-process-step"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
              >
                <div className="step-number">{step.number}</div>
                <div className="step-content">
                  <div className="step-icon">{step.icon}</div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Why Choose Us
  const WhyChooseUs: React.FC = () => {
    const benefits = [
      {
        icon: <Target size={32} />,
        title: "Precision Matching",
        description: "98% success rate in matching candidates to roles",
      },
      {
        icon: <Zap size={32} />,
        title: "Quick Turnaround",
        description: "Average placement time of 7-14 days",
      },
      {
        icon: <Shield size={32} />,
        title: "Quality Guarantee",
        description: "90-day replacement warranty on all placements",
      },
      {
        icon: <Award size={32} />,
        title: "Pre-Vetted Talent",
        description: "Comprehensive screening and background checks",
      },
      {
        icon: <TrendingUp size={32} />,
        title: "Industry Expertise",
        description: "15+ years of recruitment experience",
      },
      {
        icon: <Users size={32} />,
        title: "Large Talent Pool",
        description: "Access to 100,000+ qualified candidates",
      },
    ];

    return (
      <section className="sd_service-benefits-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">
              Why Choose Our Staffing Solutions?
            </h2>
            <p className="section-subtitle">
              Trusted by leading companies for their talent needs
            </p>
          </motion.div>

          <div className="sd_service-benefits-grid">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                className="sd_service-benefit-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="benefit-icon">{benefit.icon}</div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Job Seekers Section
  const JobSeekersSection: React.FC = () => {
    const resources = [
      "Resume Building Tips",
      "Interview Preparation",
      "Career Counseling",
      "Skill Development",
      "Market Insights",
      "Salary Guidance",
    ];

    return (
      <section className="sd_service-jobseekers-section">
        <div className="container">
          <div className="columns is-vcentered">
            <div className="column is-6">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="section-title">For Job Seekers</h2>
                <p className="section-subtitle">
                  Find your dream job with our extensive network of top
                  employers
                </p>
                <ul className="sd_service-resource-list">
                  {resources.map((resource, idx) => (
                    <li key={idx}>
                      <CheckCircle size={20} /> {resource}
                    </li>
                  ))}
                </ul>
                <div className="mt-5">
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate("/contact-us")}
                  >
                    Submit Your Resume <ArrowRight size={20} />
                  </button>
                </div>
              </motion.div>
            </div>
            <div className="column is-6">
              <motion.div
                className="sd_service-stats-card"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="stat-item">
                  <span className="stat-number">10,000+</span>
                  <span className="stat-label">Active Job Openings</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">5,000+</span>
                  <span className="stat-label">Successful Placements/Year</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">95%</span>
                  <span className="stat-label">Candidate Satisfaction</span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  // CTA Section
  const CTASection: React.FC = () => {
    return (
      <section className="sd_service-cta-section">
        <div className="container">
          <motion.div
            className="sd_service-cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="cta-title">Ready to Find the Perfect Match?</h2>
            <p className="cta-text">
              Whether you're looking to hire top talent or searching for your
              next career opportunity, we're here to help.
            </p>
            <div className="cta-buttons">
              <button
                className="btn btn-primary"
                onClick={() => navigate("/contact-us")}
              >
                Hire Talent <ArrowRight size={20} />
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate("/contact-us")}
              >
                Find Jobs
              </button>
            </div>
            <div className="cta-stats">
              <div className="stat-item">
                <span className="stat-number">15+</span>
                <span className="stat-label">Years Experience</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">1000+</span>
                <span className="stat-label">Partner Companies</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">50K+</span>
                <span className="stat-label">Placements Made</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  };

  return (
    <div className="sd_service-container">
      <HeroSection />
      <ServicesGrid />
      <IndustryExpertise />
      <RecruitmentProcess />
      <WhyChooseUs />
      <JobSeekersSection />
      <CTASection />
    </div>
  );
};

export default StaffingSolutions;
